import { computed, ref } from 'vue';
import axios from 'axios';
import vuetify from '@/plugins/vuetify';

const root = document.documentElement;
const primaryColor = ref('#d4216c');
const secondaryColor = ref('#7b2c88');
const logo = ref(null);
const favicon = ref(null);
const faviconPreview = ref(null);
const useGradient = ref(true);
const reverseGradient = ref(false);
const applicationName = ref(null);
const appNamePreview = ref(null);
const copyrightName = ref('');
const registered = ref(false);
const trademarked = ref(false);
const copyrightYear = ref(new Date().getFullYear());

const appNameDisplay = computed(() => {
    {
        let str = '';

        if (applicationName.value) {
            str += applicationName.value;
        }

        if (registered.value) {
            str += '®';
        } else if (trademarked.value) {
            str += '™';
        }

        return str;
    }
});

function setAppNamePreview(name, reg, tm) {
    let str = '';

    if (name) {
        str += name;
    }

    if (reg) {
        str += '®';
    } else if (tm) {
        str += '™';
    }

    return str;
}

const vuetifyTheme = computed(() => ({
    primary: primaryColor.value,
    secondary: secondaryColor.value
}));

function getSettingByName(settings, name) {
    return Object.values(settings).find(obj => obj.settingName === name);
}

async function setBranding() {
    root.style.setProperty('--primary-500', primaryColor.value);
    root.style.setProperty('--secondary-500', secondaryColor.value);
    Object.assign(vuetify.framework.theme.themes.light, vuetifyTheme.value);
}

async function getBranding() {
    await axios.get(`/api/branding`)
        .then(async (response) => {
            const rdWhitelabel = response?.data?.whitelabel?.settings;
            const rdApplication = response?.data?.application?.settings;

            const whiteLabelSettings = getSettingByName(rdWhitelabel, 'settingWhitelabel')?.settingArray ?? [];

            const applicationSettings = getSettingByName(rdApplication, 'settingApplication')?.settingArray ?? [];

            const responseLogo = response?.data?.logo;

            const responseFavicon = response?.data?.favicon;

            if (whiteLabelSettings.primaryColor) {
                primaryColor.value = whiteLabelSettings.primaryColor;
            }

            if (whiteLabelSettings.secondaryColor) {
                secondaryColor.value = whiteLabelSettings.secondaryColor;
            }

            useGradient.value = whiteLabelSettings.useGradient;
            reverseGradient.value = whiteLabelSettings.reverseGradient;

            applicationName.value = applicationSettings.applicationName;
            copyrightName.value = applicationSettings.copyrightName;
            trademarked.value = applicationSettings.trademarked;
            registered.value = applicationSettings.registered;

            // Set the application name in the tab.
            document.title = applicationName.value
                + (registered.value ? '®' : trademarked.value ? '™' : '');

            if (responseLogo) {
                logo.value = responseLogo;
            }

            if (responseFavicon) {
                favicon.value = responseFavicon;

                const url = favicon.value;
                if (!url) return;

                let link = document.querySelector('link[rel~=\'icon\']');
                if (!link) {
                    link = document.createElement('link');
                    link.rel = 'icon';
                    document.head.appendChild(link);
                }

                link.href = url;
            }

            await setBranding();
        });
}

function setPreviewFavicon(url = null) {
    return url ?? faviconPreview.value ?? favicon.value;
}

export function useWhiteLabel() {
    return {
        applicationName,
        appNamePreview,
        appNameDisplay,
        copyrightName,
        registered,
        trademarked,
        copyrightYear,
        primaryColor,
        secondaryColor,
        getBranding,
        logo,
        favicon,
        useGradient,
        reverseGradient,
        setPreviewFavicon,
        setAppNamePreview
    };
}

/**
 * <meta http-equiv="X-UA-Compatible"
 *           content="IE=edge" />
 *
 *     <meta name="viewport"
 *           content="width=device-width,initial-scale=1.0" />
 *     <link rel="apple-touch-icon"
 *           sizes="180x180"
 *           href="/assets/favicon/apple-touch-icon.png?v=JyAeOOKOl5">
 *     <link rel="icon"
 *           type="image/png"
 *           sizes="32x32"
 *           href="/assets/favicon/favicon-32x32.png?v=JyAeOOKOl5">
 *     <link rel="icon"
 *           type="image/png"
 *           sizes="16x16"
 *           href="/assets/favicon/favicon-16x16.png?v=JyAeOOKOl5">
 *     <link rel="manifest"
 *           href="/assets/favicon/site.webmanifest?v=JyAeOOKOl5">
 *     <link rel="mask-icon"
 *           href="/assets/favicon/safari-pinned-tab.svg?v=JyAeOOKOl5"
 *           color="#d4216c">
 *     <link rel="shortcut icon"
 *           href="/assets/favicon/favicon.ico?v=JyAeOOKOl5">
 *     <meta name="msapplication-TileColor"
 *           content="#f3f6f9">
 *     <meta name="msapplication-config"
 *           content="/assets/favicon/browserconfig.xml?v=JyAeOOKOl5">
 *     <meta name="theme-color"
 *           content="#f3f6f9">
 */